import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';
import { Category } from '../../types';

// Une catégorie dans un panneau latéral (2026-09-27), même grammaire que les
// équipes : la liste reste visible, ‹ › passe à la voisine, erreurs sous le
// champ. Le code n'est plus saisi (auto-généré depuis le nom, 2026-03-07).
//
// Sauvegarde automatique (règle du 22/09) : une catégorie ne rend rien public
// et n'écrit chez personne, chaque champ s'écrit dès qu'on le quitte s'il a
// changé. Les deux bornes d'âge partent ensemble (elles se valident l'une par
// l'autre). Seule la création garde son bouton.

export interface CategoryFormValues {
  name: string;
  display_name: string;
  gender: '' | 'M' | 'F' | 'MIXED';
  min_age: string;
  max_age: string;
  description: string;
}

/** Ce que `PUT /categories/{id}` accepte (partiel : clé absente = préservée). */
export interface CategoryPatch {
  name?: string;
  display_name?: string | null;
  gender?: string | null;
  min_age?: number | null;
  max_age?: number | null;
  description?: string;
}

const NONE = '__none';

function toValues(c: Category | null): CategoryFormValues {
  return {
    name: c?.name ?? '',
    display_name: c?.display_name ?? '',
    gender: (c?.gender as CategoryFormValues['gender']) ?? '',
    min_age: c?.min_age != null ? String(c.min_age) : '',
    max_age: c?.max_age != null ? String(c.max_age) : '',
    description: c?.description ?? '',
  };
}

const toPatch = (fields: (keyof CategoryFormValues)[], v: CategoryFormValues): CategoryPatch => {
  const p: CategoryPatch = {};
  if (fields.includes('name')) p.name = v.name.trim();
  if (fields.includes('display_name')) p.display_name = v.display_name.trim() || null;
  if (fields.includes('gender')) p.gender = v.gender || null;
  if (fields.includes('min_age')) p.min_age = v.min_age ? parseInt(v.min_age, 10) : null;
  if (fields.includes('max_age')) p.max_age = v.max_age ? parseInt(v.max_age, 10) : null;
  if (fields.includes('description')) p.description = v.description;
  return p;
};

interface Props {
  open: boolean;
  /** null = création. */
  category: Category | null;
  /** Équipes inscrites dans la catégorie (contexte de l'en-tête). */
  teamCount?: number;
  saving: boolean;
  serverError: string;
  /** Création seulement. */
  onSubmit: (values: CategoryFormValues) => void;
  /** Édition : écriture partielle, immédiate. Lève sur refus. */
  onPatch: (patch: CategoryPatch) => Promise<void>;
  onRemove: () => void;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function CategorySheet({ open, category, teamCount, saving, serverError, onSubmit, onPatch, onRemove, onClose, onDirtyChange, onPrev, onNext }: Props) {
  const { t } = useTranslation();
  const editing = !!category;

  const schema = useMemo(() => z.object({
    name: z.string().trim().min(1, t('categories.sheet.nameRequired')),
    display_name: z.string(),
    gender: z.enum(['', 'M', 'F', 'MIXED']),
    min_age: z.string(),
    max_age: z.string(),
    description: z.string(),
  }).superRefine((v, ctx) => {
    const lo = v.min_age ? parseInt(v.min_age, 10) : null;
    const hi = v.max_age ? parseInt(v.max_age, 10) : null;
    if (lo !== null && hi !== null && lo > hi) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['max_age'], message: t('categories.sheet.ageInverted') });
    }
  }), [t]);

  const form = useForm<CategoryFormValues>({ resolver: zodResolver(schema), defaultValues: toValues(category), mode: 'onTouched' });
  const autoSave = useFormAutoSave<CategoryFormValues, CategoryPatch>(form, {
    enabled: editing,
    save: onPatch,
    toPatch,
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('errors.generic')),
  });
  const { saveFields } = autoSave;

  useEffect(() => {
    autoSave.reset(toValues(category));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category?.id, open]);

  // En édition rien n'attend jamais : le garde « non enregistré » ne sert qu'à la création.
  const isDirty = !editing && form.formState.isDirty;
  useEffect(() => { onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);

  const [confirmDiscard, setConfirmDiscard] = useState<null | (() => void)>(null);
  const guard = (action: () => void) => (isDirty ? setConfirmDiscard(() => action) : action());
  const blur = (name: keyof CategoryFormValues) => () => { void saveFields(name); };

  return (
    <>
      <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) guard(onClose); }}>
        <SheetContent side="right" hideClose onInteractOutside={(e) => e.preventDefault()} className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[520px]">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full min-h-0 flex-col" noValidate>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">
                    {category
                      ? (teamCount != null ? t('categories.sheet.teamCount', { count: teamCount }) : '')
                      : t('categories.sheet.createHint')}
                  </SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                    {category ? category.name : t('categories.newCategory')}
                  </SheetTitle>
                </div>
                {category && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={() => onPrev && guard(onPrev)}><ChevronUp size={16} /></Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={() => onNext && guard(onNext)}><ChevronDown size={16} /></Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => guard(onClose)}><X size={16} /></Button>
              </div>

              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-6">
                <FormField control={form.control} name="name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categories.categoryName')}</FormLabel>
                    <FormControl><Input {...field} onBlur={() => { field.onBlur(); blur('name')(); }} placeholder={t('categories.categoryNamePlaceholder')} autoFocus={!category} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="display_name" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categories.displayName')}</FormLabel>
                    <FormControl><Input {...field} onBlur={() => { field.onBlur(); blur('display_name')(); }} placeholder={t('categories.displayNamePlaceholder')} /></FormControl>
                    <FormDescription>{t('categories.displayNameHint')}</FormDescription>
                  </FormItem>
                )} />
                <FormField control={form.control} name="gender" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('categories.gender')}</FormLabel>
                    <Select value={field.value || NONE} onValueChange={(v) => { if (v) { field.onChange(v === NONE ? '' : v); void saveFields('gender'); } }}>
                      <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                      <SelectContent>
                        <SelectItem value={NONE}>{t('categories.genderNotSpecified')}</SelectItem>
                        <SelectItem value="M">{t('categories.genderMale')}</SelectItem>
                        <SelectItem value="F">{t('categories.genderFemale')}</SelectItem>
                        <SelectItem value="MIXED">{t('categories.genderMixed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </FormItem>
                )} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="min_age" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('categories.minAge')}</FormLabel>
                      <FormControl><Input type="number" {...field} onBlur={() => { field.onBlur(); void saveFields(['min_age', 'max_age']); }} placeholder="0" min={0} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="max_age" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('categories.maxAge')}</FormLabel>
                      <FormControl><Input type="number" {...field} onBlur={() => { field.onBlur(); void saveFields(['min_age', 'max_age']); }} placeholder="∞" min={0} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="description" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.description')}</FormLabel>
                    <FormControl><Textarea {...field} onBlur={() => { field.onBlur(); blur('description')(); }} rows={6} /></FormControl>
                  </FormItem>
                )} />
                {serverError && <Alert variant="destructive"><AlertDescription>{serverError}</AlertDescription></Alert>}
              </div>

              <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
                <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                  {editing && <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />}
                </span>
                {category ? (
                  <>
                    <Button type="button" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={onRemove} disabled={saving}>
                      <Trash2 size={16} />{t('common.delete')}
                    </Button>
                    <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
                  </>
                ) : (
                  <Button type="submit" disabled={saving}>
                    {saving && <Loader2 className="animate-spin" />}
                    {t('common.create')}
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onOpenChange={(o) => { if (!o) setConfirmDiscard(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { const next = confirmDiscard; setConfirmDiscard(null); form.reset(toValues(category)); next?.(); }}
      />
    </>
  );
}
